import React from "react";
import { Marker, Popup, GeoJSON } from "react-leaflet";
import L from "leaflet";

// Prescribed (RX) burns — purple diamond markers, visually distinct from
// wildfire flames/bubbles so planned burns are never mistaken for emergencies.
const _iconCache = {};
function rxIcon(hovered) {
  const key = hovered ? "h" : "n";
  if (!_iconCache[key]) {
    const size = hovered ? 26 : 20;
    _iconCache[key] = L.divIcon({
      className: "flame-marker",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 20 20" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)); width:${size}px; height:${size}px;">
        <rect x="4.5" y="4.5" width="11" height="11" rx="2" transform="rotate(45 10 10)" fill="#9F7AEA" fill-opacity="0.9" stroke="#0A0D0C" stroke-width="1.5"/>
        <text x="10" y="13" text-anchor="middle" font-size="7.5" font-weight="900" font-family="monospace" fill="#0A0D0C">Rx</text>
      </svg>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }
  return _iconCache[key];
}

// DNR Burn Portal permitted burns — hollow purple diamond (vs filled = active RX)
function dnrIcon() {
  if (!_iconCache.dnr) {
    const size = 18;
    _iconCache.dnr = L.divIcon({
      className: "flame-marker",
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 20 20" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6)); width:${size}px; height:${size}px;">
        <rect x="4.5" y="4.5" width="11" height="11" rx="2" transform="rotate(45 10 10)" fill="#0A0D0C" fill-opacity="0.55" stroke="#9F7AEA" stroke-width="2"/>
        <circle cx="10" cy="10" r="1.8" fill="#9F7AEA"/>
      </svg>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  }
  return _iconCache.dnr;
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function RxBurnsLayer({ burns = [], dnrBurns = [], units = null }) {
  const nifcMarkers = burns.map((b) => {
    if (b.latitude == null || b.longitude == null) return null;
    return (
      <Marker
        key={`rx-${b.id}`}
        position={[b.latitude, b.longitude]}
        icon={rxIcon(false)}
        zIndexOffset={400}
      >
        <Popup>
          <div className="text-xs min-w-[180px]" data-testid={`rx-burn-popup-${b.id}`}>
            <div className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-purple-500/20 text-purple-300 border border-purple-400/50 mb-1">
              Prescribed Burn — Planned
            </div>
            <div className="font-bold text-sm mb-0.5 text-purple-300">{b.name}</div>
            <div className="text-[11px] text-white/90 space-y-0.5">
              {b.county && <div>{b.county} County</div>}
              {b.incident_size != null && <div>{Math.round(b.incident_size).toLocaleString()} acres planned/treated</div>}
              {b.discovered_at && <div>Started {fmtDate(b.discovered_at)}</div>}
            </div>
            <div className="text-[10px] text-white/80 mt-1.5 font-semibold">
              Intentional, managed burn — not an emergency. Smoke may be visible.
            </div>
          </div>
        </Popup>
      </Marker>
    );
  });

  const dnrMarkers = dnrBurns.map((b) => {
    if (b.latitude == null || b.longitude == null) return null;
    return (
      <Marker
        key={`dnrrx-${b.id}`}
        position={[b.latitude, b.longitude]}
        icon={dnrIcon()}
        zIndexOffset={390}
      >
        <Popup>
          <div className="text-xs min-w-[180px]" data-testid={`dnr-burn-popup-${b.id}`}>
            <div className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-purple-500/20 text-purple-300 border border-purple-400/50 mb-1">
              DNR Permitted Burn{b.status === "Submitted" ? " — Requested" : ""}
            </div>
            <div className="font-bold text-sm mb-0.5 text-purple-300">
              {b.burn_type ? `${b.burn_type} burn` : "Permitted burn"}
              {b.acres != null ? ` · ${Math.round(b.acres).toLocaleString()} ac` : ""}
            </div>
            <div className="text-[11px] text-white/90 space-y-0.5">
              {b.county && <div className="capitalize">{b.county} County</div>}
              {b.planned_date && (
                <div>
                  Planned ignition {fmtDate(b.planned_date)}
                  {b.planned_time ? ` at ${b.planned_time.slice(0, 5)}` : ""}
                  {b.multi_day ? " (multi-day)" : ""}
                </div>
              )}
              {b.permit_number && <div>Permit {b.permit_number} · {b.status}</div>}
            </div>
            <div className="text-[10px] text-white/80 mt-1.5 font-semibold">
              Permitted via WA DNR Burn Portal — planned, not an emergency.
            </div>
          </div>
        </Popup>
      </Marker>
    );
  });

  // Exact USFS FACTS burn-unit boundaries near the active burn points
  const unitPolys = units?.features?.length ? (
    <GeoJSON
      key={`rx-units-${units.features.length}`}
      data={units}
      style={() => ({
        color: "#9F7AEA",
        weight: 1.5,
        dashArray: "5 4",
        fillColor: "#9F7AEA",
        fillOpacity: 0.08,
      })}
      onEachFeature={(feature, layer) => {
        const p = feature.properties || {};
        const acres = p.gis_acres != null ? `${Math.round(p.gis_acres).toLocaleString()} ac` : "";
        layer.bindPopup(
          `<div style="font-size:12px;min-width:170px">
            <div style="display:inline-block;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;padding:2px 6px;border-radius:2px;background:rgba(159,122,234,0.2);color:#D8B4FE;border:1px solid rgba(216,180,254,0.5);margin-bottom:4px">USFS Planned Burn Unit</div>
            <div style="font-weight:700;font-size:13px;color:#D8B4FE">${p.treatment_name || p.treatment_type || "Burn unit"}</div>
            <div style="font-size:11px;color:rgba(255,255,255,0.9)">${p.treatment_type || ""}${acres ? ` · ${acres}` : ""}${p.fiscal_year_planned ? ` · FY ${p.fiscal_year_planned}` : ""}</div>
            <div style="font-size:10px;color:rgba(255,255,255,0.8);margin-top:4px;font-weight:600">Exact unit boundary from USFS FACTS — timing is fiscal-year level.</div>
          </div>`
        );
      }}
    />
  ) : null;

  return (
    <>
      {unitPolys}
      {nifcMarkers}
      {dnrMarkers}
    </>
  );
}
